import * as yup from "yup";
import _ from "lodash";
import { text as textSchema } from "../schemas";

// Mismo whitelist que el backend (api/src/utils/validators/password.ts) -- deben coincidir
// carácter por carácter, si no un password puede pasar acá y ser rechazado por el backend
// (o viceversa). "ñ"/tildes cuentan como letra, no como carácter especial -- es como las
// tipea naturalmente un hispanohablante.
const hasUppercase = /[A-ZÁÉÍÓÚÑÜ]/;
const hasLowercase = /[a-záéíóúñü]/;
const hasNumber = /[0-9]/;
const hasSpecialChar = /[!"#$%&'()*+,\-./:;<=>?@[\]^_`{|}~\\]/;

// Mismo límite que PASSWORD_MAX_LENGTH en api/src/utils/validators/password.ts, forzado ahí
// vía @MaxLength en los DTOs (fuera del passwordPolicyViolations que da errores por regla) --
// sin este .max() acá, una contraseña de más de 128 caracteres pasa el form y llega al backend
// como un 400 crudo de ValidationPipe, que las pantallas de alta/reset muestran como error genérico.
const PASSWORD_MAX_LENGTH = 128;

// Mensajes en inglés que puede devolver `api` para el campo password -- ya sea de
// passwordPolicyViolations (409 Conflict) o de los decoradores de class-validator en los DTOs
// (400 Bad Request, ej. el @MaxLength de arriba). Se traducen para no mostrarle al usuario texto
// en inglés cuando el rechazo viene del backend en vez de esta validación de yup (ej. la regla de
// "no repetir la contraseña actual", que no se puede validar del lado del cliente).
const BACKEND_ERROR_MESSAGES = {
  "password is required": "La contraseña es obligatoria",
  // "password is required" es el mensaje de passwordPolicyViolations, pero @IsNotEmpty()
  // de class-validator corta antes en los DTOs (POST /users, passwordRecovery/change) con
  // su propio mensaje default -- se traduce también por si algún día se llega a este
  // punto (ej. un endpoint que use passwordPolicyViolations sin @IsNotEmpty() previo).
  "password should not be empty": "La contraseña es obligatoria",
  "password must be at least 10 characters long": "La contraseña debe tener al menos 10 caracteres",
  [`password must be shorter than or equal to ${PASSWORD_MAX_LENGTH} characters`]: `La contraseña no puede tener más de ${PASSWORD_MAX_LENGTH} caracteres`,
  "password must contain at least one uppercase letter": "La contraseña debe tener al menos una mayúscula",
  "password must contain at least one lowercase letter": "La contraseña debe tener al menos una minúscula",
  "password must contain at least one number": "La contraseña debe tener al menos un número",
  "password must contain at least one special character": "La contraseña debe tener al menos un carácter especial",
  "password must not contain the document number": "La contraseña no puede contener tu número de documento",
  "password must not contain the license number": "La contraseña no puede contener tu número de matrícula",
  "password must not contain the email": "La contraseña no puede contener tu email",
  "new password must be different from the current password": "La nueva contraseña tiene que ser distinta de la anterior.",
};

// Extrae de un error de axios los mensajes de `response.data.message` (string o array,
// según si el backend respondió con un Conflict o con el ValidationPipe default) que
// correspondan a la política de contraseña, ya traducidos. Es todo o nada: si el array
// trae algún mensaje no reconocido (ej. otro campo del form inválido en la misma
// respuesta), devuelve [] en vez de una traducción parcial -- mostrar solo el error de
// password cuando en realidad hay más cosas mal sería engañoso, mejor que el llamador
// caiga a su mensaje genérico.
export const passwordBackendErrorMessages = (error) => {
  const rawMessages = _.chain(error).get("response.data.message").castArray().compact().value();
  const translatedMessages = rawMessages.map((message) => BACKEND_ERROR_MESSAGES[message]);
  return translatedMessages.every((message) => !!message) ? translatedMessages : [];
};

// documentNumber/licenseNumber/email solo están disponibles como campos
// hermanos cuando este schema se usa en el alta (register/schema.js). En el
// restablecimiento de contraseña (passwordRecovery/change.jsx) el frontend no
// tiene esos datos del usuario (solo un securitySignature), así que ahí el
// chequeo se omite y la regla la aplica el backend, que es la fuente de verdad.
const containsSiblingValue = (password, siblingValue) =>
  !!siblingValue && String(password).toLowerCase().includes(String(siblingValue).toLowerCase());

const passwordValidations = {
  password: textSchema
    .min(10, 'La contraseña debe tener al menos 10 caracteres')
    .max(PASSWORD_MAX_LENGTH, `La contraseña no puede tener más de ${PASSWORD_MAX_LENGTH} caracteres`)
    .matches(hasUppercase, { message: 'La contraseña debe tener al menos una mayúscula', excludeEmptyString: true })
    .matches(hasLowercase, { message: 'La contraseña debe tener al menos una minúscula', excludeEmptyString: true })
    .matches(hasNumber, { message: 'La contraseña debe tener al menos un número', excludeEmptyString: true })
    .matches(hasSpecialChar, { message: 'La contraseña debe tener al menos un carácter especial', excludeEmptyString: true })
    .test(
      'no-document-number',
      'La contraseña no puede contener tu número de documento',
      function (value) {
        return !containsSiblingValue(value, this.parent.documentNumber);
      }
    )
    .test(
      'no-license-number',
      'La contraseña no puede contener tu número de matrícula',
      function (value) {
        return !containsSiblingValue(value, this.parent.licenseNumber);
      }
    )
    .test(
      'no-email',
      'La contraseña no puede contener tu email',
      function (value) {
        return !containsSiblingValue(value, this.parent.email);
      }
    ),
  passwordConfirmation: textSchema
    .oneOf([yup.ref('password')], 'Las contraseñas no coinciden'),
};

export default passwordValidations;
